import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Button from "../../atoms/Button";
import { CloseIcon } from "../../atoms/Iconography/NavigationControls";
import UserCardAvatar from "../../molecules/UserCardAvatar";
import PopupOverlay from "../Background/PopupOverlay";
import "./BandMemberInviteModal.scss";

export default function BandMemberInviteModal({
    isOpen,
    bandName,
    searchTerm,
    onSearchChange,
    candidates = [],
    isLoading = false,
    invitingUserId = null,
    error = null,
    onInvite,
    onClose,
}) {
    const { t } = useTranslation();

    if (!isOpen) {
        return null;
    }

    const portalTarget = typeof document !== "undefined"
        ? document.getElementById("portal") || document.body
        : null;
    const content = (
        <PopupOverlay onClose={onClose} closeOnOverlayClick closeOnEsc>
            <div
                className="band-member-invite-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="bandMemberInviteTitle"
            >
                <header className="band-member-invite-modal__header">
                    <div>
                        <h2 id="bandMemberInviteTitle">{t("bandPage.inviteMember.title")}</h2>
                        <p>{t("bandPage.inviteMember.subtitle", { name: bandName || t("bandPage.inviteMember.bandFallback") })}</p>
                    </div>
                    <button
                        type="button"
                        className="band-member-invite-modal__close"
                        onClick={onClose}
                        aria-label={t("bandPage.inviteMember.close")}
                        disabled={Boolean(invitingUserId)}
                    >
                        <CloseIcon aria-hidden="true" focusable="false" />
                    </button>
                </header>

                <label className="band-member-invite-modal__search">
                    <span>{t("bandPage.inviteMember.searchLabel")}</span>
                    <input
                        type="search"
                        value={searchTerm}
                        onChange={(event) => onSearchChange?.(event.target.value)}
                        placeholder={t("bandPage.inviteMember.searchPlaceholder")}
                        autoFocus
                    />
                </label>

                {error ? (
                    <p className="band-member-invite-modal__error" role="alert">
                        {t("bandPage.inviteMember.error")}
                    </p>
                ) : null}

                <div className="band-member-invite-modal__list" role="list">
                    {isLoading ? (
                        <p className="band-member-invite-modal__empty" role="status">
                            {t("bandPage.inviteMember.loading")}
                        </p>
                    ) : candidates.length > 0 ? (
                        candidates.map((candidate) => {
                            const isInviting = invitingUserId === candidate.userId;
                            return (
                                <article
                                    key={candidate.userId}
                                    className="band-member-invite-modal__candidate"
                                    role="listitem"
                                >
                                    <UserCardAvatar
                                        userId={candidate.userId}
                                        username={candidate.username || candidate.displayName}
                                        avatarUrl={candidate.avatarUrl || undefined}
                                        alt={candidate.displayName || candidate.username || t("bandPage.inviteMember.avatarAlt")}
                                        className="band-member-invite-modal__avatar"
                                        avatarClassName="band-member-invite-modal__avatar-media"
                                        enableProfilePreview={false}
                                    />
                                    <div className="band-member-invite-modal__identity">
                                        <strong>{candidate.displayName || candidate.username}</strong>
                                        {candidate.username ? <span>@{candidate.username}</span> : null}
                                    </div>
                                    <Button
                                        text={isInviting ? t("bandPage.inviteMember.inviting") : t("bandPage.inviteMember.invite")}
                                        variant="primary"
                                        size="small"
                                        shape="pill"
                                        onClick={() => onInvite?.(candidate)}
                                        disabled={Boolean(invitingUserId)}
                                    />
                                </article>
                            );
                        })
                    ) : (
                        <p className="band-member-invite-modal__empty">
                            {searchTerm
                                ? t("bandPage.inviteMember.emptySearch")
                                : t("bandPage.inviteMember.emptyFriends")}
                        </p>
                    )}
                </div>
            </div>
        </PopupOverlay>
    );

    return portalTarget ? ReactDOM.createPortal(content, portalTarget) : content;
}

BandMemberInviteModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    bandName: PropTypes.string,
    searchTerm: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    candidates: PropTypes.arrayOf(PropTypes.shape({
        userId: PropTypes.string.isRequired,
        username: PropTypes.string,
        displayName: PropTypes.string,
        avatarUrl: PropTypes.string,
    })),
    isLoading: PropTypes.bool,
    invitingUserId: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object, PropTypes.string]),
    onInvite: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
